import React, { useEffect, useRef, useState } from 'react';
import { Settings, Users, Wrench, RotateCw, Package, HeadphonesIcon, CheckCircle2, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { servicios } from '../data/mockData';

export const Servicios = () => {
  const iconMap = {
    Settings: Settings,
    Users: Users,
    Wrench: Wrench,
    RotateCw: RotateCw,
    Package: Package,
    HeadphonesIcon: HeadphonesIcon
  };

  const procesoPaso = [
    {
      step: "01",
      title: "Consulta Inicial",
      description: "Reunión para entender tus necesidades, objetivos y restricciones del proyecto"
    },
    {
      step: "02",
      title: "Diseño y Propuesta",
      description: "Desarrollo de propuesta técnica detallada con especificaciones y cotización"
    },
    {
      step: "03",
      title: "Fabricación",
      description: "Manufactura con materiales de primera calidad y estricto control de calidad"
    },
    {
      step: "04",
      title: "Instalación",
      description: "Instalación profesional, puesta en marcha y capacitación de personal"
    },
    {
      step: "05",
      title: "Soporte Continuo",
      description: "Acompañamiento post-instalación, mantenimiento y disponibilidad de refacciones"
    }
  ];

  // Estado para controlar qué pasos están visibles
  const [visibleSteps, setVisibleSteps] = useState([]);
  const stepRefs = useRef([]);

  useEffect(() => {
    const observers = [];
    
    stepRefs.current.forEach((ref, index) => {
      if (ref) {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && !visibleSteps.includes(index)) {
                // Agregar el paso con un delay progresivo
                setTimeout(() => {
                  setVisibleSteps((prev) => {
                    if (!prev.includes(index)) {
                      return [...prev, index];
                    }
                    return prev;
                  });
                }, index * 200); // 200ms de delay entre cada número
              }
            });
          },
          { threshold: 0.3 }
        );
        
        observer.observe(ref);
        observers.push(observer);
      }
    });

    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-96 h-96 bg-white rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-72 h-72 bg-blue-400 rounded-full filter blur-3xl"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Nuestros <span className="text-blue-300">Servicios</span>
            </h1>
            <p className="text-xl text-blue-100 leading-relaxed">
              Soluciones integrales que acompañan tu proyecto desde el diseño hasta el soporte continuo
            </p>
          </div>
        </div>
      </section>

      {/* Servicios Grid */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Servicios <span className="text-blue-600">Especializados</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Cobertura completa para todas tus necesidades de maquinaria industrial
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {servicios.map((servicio) => {
              const IconComponent = iconMap[servicio.icon];
              return (
                <div
                  key={servicio.id}
                  className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border-2 border-transparent hover:border-blue-600 group"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-blue-800 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-blue-600 transition-colors">
                    {servicio.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {servicio.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Proceso Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Nuestro <span className="text-blue-600">Proceso</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Metodología probada que garantiza resultados excepcionales en cada proyecto
            </p>
          </div>

          <div className="relative">
            {/* Connecting Line */}
            <div className="hidden lg:block absolute top-16 left-0 right-0 h-1 bg-gradient-to-r from-blue-200 via-blue-600 to-blue-200 z-0"></div>

            <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-8 relative z-10">
              {procesoPaso.map((paso, index) => (
                <div 
                  key={index} 
                  className="text-center"
                  ref={(el) => (stepRefs.current[index] = el)}
                >
                  <div className={`w-32 h-32 mx-auto mb-6 bg-gradient-to-br from-blue-600 to-blue-800 rounded-full flex items-center justify-center text-white shadow-xl relative transition-all duration-700 ease-out ${
                    visibleSteps.includes(index) ? 'scale-100 opacity-100' : 'scale-0 opacity-0'
                  }`}>
                    {visibleSteps.includes(index) && (
                      <div className="absolute inset-0 bg-white rounded-full animate-ping opacity-20"></div>
                    )}
                    <span className="text-4xl font-bold relative z-10">{paso.step}</span>
                  </div>
                  <h3 className={`text-xl font-bold text-gray-900 mb-3 transition-all duration-500 delay-100 ${
                    visibleSteps.includes(index) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                  }`}>{paso.title}</h3>
                  <p className={`text-gray-600 text-sm leading-relaxed transition-all duration-500 delay-200 ${
                    visibleSteps.includes(index) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                  }`}>{paso.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Beneficios Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Beneficios de Nuestros{' '}
                <span className="text-blue-600">Servicios</span>
              </h2>
              <div className="space-y-4">
                {[
                  "Reducción de tiempos muertos en tu producción",
                  "Optimización de recursos y costos operativos",
                  "Mejora en la calidad y presentación de productos",
                  "Aumento de la capacidad productiva",
                  "Soluciones escalables que crecen con tu negocio",
                  "Soporte técnico confiable y rápido",
                  "Capacitación completa para tu equipo",
                  "Garantía de fabricación y refacciones disponibles"
                ].map((beneficio, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                    <p className="text-lg text-gray-700">{beneficio}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="/images/servicios-beneficios.jpg"
                alt="Servicios MAESI"
                className="rounded-2xl shadow-2xl w-full h-[600px] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl max-w-xs">
                <div className="flex items-center gap-3 mb-2">
                  <HeadphonesIcon className="w-8 h-8 text-blue-600" />
                  <div className="text-2xl font-bold text-gray-900">24/7</div>
                </div>
                <p className="text-gray-600 font-medium">
                  Soporte técnico disponible cuando lo necesites
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Garantías Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Nuestras <span className="text-blue-600">Garantías</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Compromisos que respaldan la calidad de nuestros servicios
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-blue-600 to-blue-800 p-8 rounded-2xl text-white shadow-xl">
              <Settings className="w-12 h-12 mb-4" />
              <h3 className="text-2xl font-bold mb-3">Calidad Certificada</h3>
              <p className="text-blue-100 leading-relaxed">
                Todos nuestros productos cumplen con estándares internacionales de calidad y seguridad industrial
              </p>
            </div>

            <div className="bg-gradient-to-br from-gray-900 to-blue-950 p-8 rounded-2xl text-white shadow-xl">
              <Wrench className="w-12 h-12 mb-4" />
              <h3 className="text-2xl font-bold mb-3">Garantía de Fábrica</h3>
              <p className="text-gray-300 leading-relaxed">
                Cobertura completa contra defectos de fabricación con términos claros y soporte inmediato
              </p>
            </div>

            <div className="bg-gradient-to-br from-blue-700 to-blue-900 p-8 rounded-2xl text-white shadow-xl">
              <Package className="w-12 h-12 mb-4" />
              <h3 className="text-2xl font-bold mb-3">Refacciones Originales</h3>
              <p className="text-blue-100 leading-relaxed">
                Disponibilidad garantizada de refacciones y componentes originales para tu maquinaria
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 via-blue-700 to-blue-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-300 rounded-full filter blur-3xl"></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            ¿Listo para Optimizar tu Producción?
          </h2>
          <p className="text-xl mb-8 text-blue-100 max-w-2xl mx-auto leading-relaxed">
            Nuestro equipo de expertos está listo para asesorarte y diseñar la solución perfecta para tu empresa
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contacto"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-blue-900 font-bold rounded-lg hover:bg-blue-50 transition-all duration-200 shadow-xl hover:shadow-2xl transform hover:-translate-y-1"
            >
              Solicitar Asesoría
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/equipos"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-blue-800 text-white font-bold rounded-lg hover:bg-blue-900 transition-all duration-200 shadow-xl hover:shadow-2xl transform hover:-translate-y-1 border-2 border-white"
            >
              Ver Equipos
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
